// The signup and invitation form behaviour, shared by signup_controller.js and
// invitations_controller.js.
//
// Deliberately NOT wired through the shared `validation` controller: two of its checks -
// password strength (which reads password_requirements_controller.js's `.satisfied` output) and
// password match (which compares two fields) - have no equivalent in `validation`'s
// one-rule-per-field contract. Its inline errors are also blur-only to both arm and clear, with
// no live re-check on `input`.
const passwordMeetsCriteria = (form) => {
  return form.find(".password-requirement .satisfied").length == 4;
};

const passwordsMatch = (form) => {
  let passwords = form.find(".form-control.password");
  if (!passwords) { return true; }

  return passwords[0].value == passwords[1].value;
};

const validateEmail = (form) => {
  let emailInput = form.find(".form-control.email")[0];
  if (!emailInput) { return null; }

  let email = emailInput.value;
  let regexp = new RegExp("^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|\"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*\")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)\\])$");

  return !!email.match(regexp);
};

const termsAccepted = (form) => {
  let termsCheckbox = form.find("#user_terms_of_service")[0];
  if (!termsCheckbox) { return null; }

  return termsCheckbox.checked;
};

const validateForm = (form, values) => {
  let checks = [];

  if (values.includes("")) {
    return false;
  }

  checks.push(validateEmail(form));
  checks.push(passwordMeetsCriteria(form));
  checks.push(passwordsMatch(form));
  checks.push(termsAccepted(form));

  return !checks.includes(false);
};

const setInvalidEmailHandler = (form) => {
  let emailInput = form.find(".form-control.email")[0];
  if (!emailInput) { return null; }

  let errorMessage = document.createElement("span");

  errorMessage.classList.add("help-block");
  errorMessage.textContent = "invalid email";

  emailInput.addEventListener("focusout", () => {
    if (!validateEmail(form)) {
      emailInput.closest(".form-group").classList.add("has-error");
      emailInput.closest(".form-group").appendChild(errorMessage);
    } else {
      emailInput.closest(".form-group").classList.remove("has-error");
      errorMessage.remove();
    }
  });
};

const setInvalidPasswordHandler = (form) => {
  let passwordInput = form.find(".user_password .form-control")[0];
  if (!passwordInput) { return null; }

  let errorMessage = document.createElement("span");

  errorMessage.classList.add("help-block");
  errorMessage.textContent = "invalid password";

  passwordInput.addEventListener("focusout", () => {
    if (!passwordMeetsCriteria(form)) {
      passwordInput.closest(".form-group").classList.add("has-error");
      passwordInput.closest(".form-group").appendChild(errorMessage);
    } else {
      passwordInput.closest(".form-group").classList.remove("has-error");
      errorMessage.remove();
    }
  });
};

const setInvalidPasswordConfirmationHandler = (form) => {
  let passwordConfirmationInput = form.find(".user_password_confirmation .form-control")[0];
  if (!passwordConfirmationInput) { return null; }

  let errorMessage = document.createElement("span");

  errorMessage.classList.add("help-block");
  errorMessage.textContent = "passwords don't match";

  passwordConfirmationInput.addEventListener("focusout", () => {
    if (!passwordsMatch(form)) {
      passwordConfirmationInput.closest(".form-group").classList.add("has-error");
      passwordConfirmationInput.closest(".form-group").appendChild(errorMessage);
    } else {
      passwordConfirmationInput.closest(".form-group").classList.remove("has-error");
      errorMessage.remove();
    }
  });
};

const setTermsCheckboxHandler = (form) => {
  let termsCheckbox = form.find("#user_terms_of_service")[0];
  let errorMessage = document.createElement("span");

  errorMessage.classList.add("help-block");
  errorMessage.textContent = "You must accept the terms and conditions";

  termsCheckbox.addEventListener("focusout", () => {
    if (!termsAccepted(form)) {
      termsCheckbox.closest(".form-group").classList.add("has-error");
      termsCheckbox.closest(".form-group").appendChild(errorMessage);
    } else {
      termsCheckbox.closest(".form-group").classList.remove("has-error");
      errorMessage.remove();
    }
  });
};

const changePasswordInput = (element) => {
  const icon = element.closest(".input-addon-group").querySelector(".input-addon.addon-append i");

  if (element.type === "password") {
    element.type = "text";
    icon.classList.remove("icon-lasso-show");
    icon.classList.add("icon-lasso-hide");
  } else {
    element.type = "password";
    icon.classList.remove("icon-lasso-hide");
    icon.classList.add("icon-lasso-show");
  }
};

export default class SignupFormHelper {
  static enableFormValidation(form) {
    const termsCheckbox = form.find("#user_terms_of_service")[0];
    const inputs = form.find(".form-control.optional").toArray();
    const btn = form.find(".btn-secondary")[0];

    inputs.forEach((input) => {
      input.addEventListener("input", () => {
        let values = [];
        inputs.forEach(v => values.push(v.value));

        if (validateForm(form, values)) {
          btn.disabled = false;
        } else {
          btn.disabled = true;
        }
      });
    });

    setInvalidEmailHandler(form);
    setInvalidPasswordHandler(form);
    setInvalidPasswordConfirmationHandler(form);
    if (termsCheckbox) { setTermsCheckboxHandler(form); }
  }

  static initTermsModal(termsModal, form) {
    form.find("#user_terms_of_service").val(1);
  }

  static showTermsModal(termsModal) {
    $(termsModal).modal("show");
  }

  static hideTermsModal(termsModal) {
    $(termsModal).modal("hide");
  }

  static printTerms(termsSelector) {
    let $termsAndConditions = $(termsSelector);
    let termsWindow = window.open("", "Terms & Conditions", "height=400,width=600");

    termsWindow.document.write("<html><head><title>" + document.title + "</title>");
    termsWindow.document.write("</head><body>");
    termsWindow.document.write($termsAndConditions[0].innerHTML);
    termsWindow.document.write("</body></html>");
    termsWindow.document.close();

    termsWindow.focus();
    termsWindow.print();
    termsWindow.close();

    return true;
  }

  static togglePasswordConfirmationInput(form) {
    let input;

    if (form === "business") {
      input = $(".business-form .user_password_confirmation .form-control")[0];
    } else {
      input = $(".personal-form .user_password_confirmation .form-control")[0];
    }

    changePasswordInput(input);
  }
}
